import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, ArrowUpRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import TableToolbar, { useVisibleColumns } from "@/components/TableToolbar";

const ALL_COLUMNS = [
  { key: "case", label: "Case #", alwaysOn: true },
  { key: "subject", label: "Subject" },
  { key: "garage", label: "Garage" },
  { key: "priority", label: "Priority" },
  { key: "assignee", label: "Assignee" },
  { key: "updated", label: "Updated" },
  { key: "status", label: "Status", alwaysOn: true },
];

const VIEWS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "waiting_on_garage", label: "Waiting" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];

const PRIORITY_TONE = {
  urgent: "bg-[#e63946]/10 text-[#e63946]",
  high: "bg-[#F79009]/10 text-[#F79009]",
  normal: "bg-[#457b9d]/10 text-[#457b9d]",
  low: "bg-[#66708515] text-[#667085]",
};

const STATUS_TONE = {
  open: "bg-[#F79009]/10 text-[#F79009]",
  in_progress: "bg-[#457b9d]/10 text-[#457b9d]",
  waiting_on_garage: "bg-[#e63946]/10 text-[#e63946]",
  resolved: "bg-[#12B76A]/10 text-[#12B76A]",
  closed: "bg-[#66708515] text-[#667085]",
};

const PriorityPill = ({ p }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${PRIORITY_TONE[p] || PRIORITY_TONE.normal}`}>
    {p || "normal"}
  </span>
);
const StatusPill = ({ s }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[s] || STATUS_TONE.open}`}>
    {(s || "open").replace(/_/g, " ")}
  </span>
);

export default function AdminSupportCases() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [priorityF, setPriorityF] = useState("all");
  const [visibleCols, toggleCol] = useVisibleColumns("admin_support_cases", ALL_COLUMNS);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (view !== "all") params.status_f = view;
    if (priorityF !== "all") params.priority_f = priorityF;
    api.get("/admin/support/cases", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [view, priorityF]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const isVisible = (k) => ALL_COLUMNS.find((c) => c.key === k)?.alwaysOn || visibleCols.has(k);

  return (
    <div className="space-y-5" data-testid="admin-support-cases-root">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Support</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
          <LifeBuoy size={22} className="text-[#457b9d]" /> Support Cases
        </h1>
        <p className="text-[13px] text-[#667085] mt-0.5">
          Tickets raised by garage users — bugs, how-to, billing, feature requests.
        </p>
      </div>

      <TableToolbar
        testIdPrefix="admin-support-cases"
        searchValue={q}
        onSearchChange={setQ}
        searchPlaceholder="Search subject, case #, description…"
        views={VIEWS}
        activeView={view}
        onViewChange={setView}
        columns={ALL_COLUMNS}
        visibleColumns={visibleCols}
        onToggleColumn={toggleCol}
        right={
          <select
            value={priorityF}
            onChange={(e) => setPriorityF(e.target.value)}
            data-testid="admin-support-priority-filter"
            className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
          >
            <option value="all">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        }
      />

      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-support-cases-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Case #</th>
              {isVisible("subject") && <th className="text-left px-4 py-3 font-semibold">Subject</th>}
              {isVisible("garage") && <th className="text-left px-4 py-3 font-semibold">Garage</th>}
              {isVisible("priority") && <th className="text-left px-4 py-3 font-semibold">Priority</th>}
              {isVisible("assignee") && <th className="text-left px-4 py-3 font-semibold">Assignee</th>}
              {isVisible("updated") && <th className="text-left px-4 py-3 font-semibold">Updated</th>}
              <th className="text-left px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[13px] text-[#667085]">No cases in this view.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-[#F7F9FC]" data-testid={`admin-support-row-${c.id}`}>
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/support/cases/${c.id}`}
                    data-testid={`admin-support-link-${c.id}`}
                    className="inline-flex items-center gap-2 text-[#1d3557] hover:text-[#e63946] font-semibold font-mono text-[12px]"
                  >
                    <LifeBuoy size={13} className="text-[#457b9d]" /> {c.case_number}
                  </Link>
                </td>
                {isVisible("subject") && (
                  <td className="px-4 py-3">
                    <div className="text-[13px] text-[#1d3557] font-semibold truncate max-w-md" title={c.subject}>
                      {c.subject}
                    </div>
                    <div className="text-[11px] text-[#667085] uppercase tracking-wider font-semibold">
                      {c.category?.replace(/_/g, " ")} · by {c.opened_by_email}
                    </div>
                  </td>
                )}
                {isVisible("garage") && (
                  <td className="px-4 py-3 text-[12px]">
                    {c.garage ? (
                      <Link
                        to={`/admin/garages/${c.garage.id}`}
                        className="inline-flex items-center gap-1 text-[#457b9d] hover:text-[#e63946] font-semibold"
                      >
                        {c.garage.name} <ArrowUpRight size={11} />
                      </Link>
                    ) : "—"}
                  </td>
                )}
                {isVisible("priority") && <td className="px-4 py-3"><PriorityPill p={c.priority} /></td>}
                {isVisible("assignee") && (
                  <td className="px-4 py-3 text-[12px] text-[#667085] font-mono">
                    {c.assignee_email || <span className="text-[#a8dadc]">Unassigned</span>}
                  </td>
                )}
                {isVisible("updated") && (
                  <td className="px-4 py-3 text-[11px] font-mono text-[#667085]">{fmtDateTime(c.updated_at)}</td>
                )}
                <td className="px-4 py-3"><StatusPill s={c.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
